import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Music, X, Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useMusicPlayer } from '@/context/MusicPlayerContext';
import { useAuth } from '@/context/AuthContext';
import { useTabBarHidden } from '@/hooks/useTabBarVisibility';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default fallback URL
const DEFAULT_SOUNDCLOUD_URL = 'https://soundcloud.com/djdennyhus/dancehall-february-mix-2026-wyfl-ft-malie-donn-vybz-kartel-masicka-valiant-armani';

const GlobalMusicPlayer = () => {
  const { isVisible, showPlayer, hidePlayer } = useMusicPlayer();
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const isSafeSpace = location.pathname === '/safe-space';
  const isCrisisPage = location.pathname === '/blackout';
  const hidePlayer_path = isSafeSpace || isCrisisPage;
  const [soundcloudUrl, setSoundcloudUrl] = useState(DEFAULT_SOUNDCLOUD_URL);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const userPrefRef = useRef(
    // Seed from localStorage so we don't flash the default before the auth
    // context populates user.preferred_music_genre.
    (() => {
      try { return localStorage.getItem('rollm8s_music_genre') || null; } catch { return null; }
    })()
  );
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Music icon sits above the mobile tab bar (if present) with a small gap.
  // On pages WITHOUT a tab bar (landing page, login etc.), it sits close to
  // the bottom edge so it doesn't float awkwardly in empty space.
  // When the tab bar auto-hides on scroll, this drops too (follows it out).
  //   - With visible tab bar: calc(56px + env(safe-area-inset-bottom) + 12px)
  //   - No/hidden tab bar:    calc(env(safe-area-inset-bottom) + 16px)
  const BOTTOM_WITH_TABBAR = 'calc(56px + env(safe-area-inset-bottom, 0px) + 12px)';
  const BOTTOM_NO_TABBAR = 'calc(env(safe-area-inset-bottom, 0px) + 16px)';
  const tabBarHidden = useTabBarHidden();

  const resolveDefaultBottom = () => {
    if (tabBarHidden) return BOTTOM_NO_TABBAR;
    if (typeof document === 'undefined') return BOTTOM_WITH_TABBAR;
    const tabBar = document.querySelector('[data-testid="mobile-tab-bar"]');
    // Only treat the tab bar as present if it's actually rendered (not md:hidden on desktop)
    if (tabBar && tabBar.offsetParent !== null) return BOTTOM_WITH_TABBAR;
    return BOTTOM_NO_TABBAR;
  };

  // pos.bottom is either a string (default CSS calc) OR a number (after user drags).
  const [pos, setPos] = useState({ bottom: BOTTOM_NO_TABBAR, left: 16 });
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, bottom: 0, left: 0 });

  // Reset to the appropriate default on every page navigation or tab-bar
  // visibility change. User drags don't persist across routes — each new
  // page snaps the icon back to its default spot.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setPos({ bottom: resolveDefaultBottom(), left: 16 });
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tabBarHidden]);

  const handleDragStart = useCallback((e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    isDragging.current = false;
    // pos.bottom may be a CSS calc() string; resolve it to a number (px from
    // viewport bottom) by measuring the element's current rendered position.
    const target = e.currentTarget || dragRef.current;
    let currentBottomPx = typeof pos.bottom === 'number' ? pos.bottom : 0;
    if (target && typeof pos.bottom === 'string') {
      const rect = target.getBoundingClientRect();
      currentBottomPx = Math.round(window.innerHeight - rect.bottom);
    }
    dragStart.current = { x: clientX, y: clientY, bottom: currentBottomPx, left: pos.left };
    
    const handleMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - dragStart.current.x;
      const dy = cy - dragStart.current.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) isDragging.current = true;
      const newLeft = Math.max(0, Math.min(window.innerWidth - 56, dragStart.current.left + dx));
      const newBottom = Math.max(0, Math.min(window.innerHeight - 56, dragStart.current.bottom - dy));
      setPos({ bottom: newBottom, left: newLeft });
    };
    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [pos]);
  
  // Playback control state
  const [isPlaying, setIsPlaying] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const pendingAutoplayRef = useRef(false);

  // Listen for autoplay request from splash screen
  useEffect(() => {
    const handleAutoplay = () => {
      showPlayer();
      pendingAutoplayRef.current = true;

      // Use user's preferred genre, fall back to Dancehall, then default URL
      const preferredGenreId = user?.preferred_music_genre;
      const preferredGenre = preferredGenreId 
        ? genres.find(g => g.genre_id === preferredGenreId)
        : null;
      const fallbackGenre = genres.find(g => g.genre_id === 'bashment' || g.name?.toLowerCase() === 'bashment');
      const targetGenre = preferredGenre || fallbackGenre;
      const targetUrl = targetGenre?.url || DEFAULT_SOUNDCLOUD_URL;

      if (targetGenre) {
        setSelectedGenre(targetGenre);
      }

      if (soundcloudUrl === targetUrl && widgetRef.current && widgetReady) {
        widgetRef.current.play();
        pendingAutoplayRef.current = false;
      } else {
        setSoundcloudUrl(targetUrl);
      }
    };
    window.addEventListener('autoplayMusic', handleAutoplay);
    return () => window.removeEventListener('autoplayMusic', handleAutoplay);
  }, [widgetReady, showPlayer, soundcloudUrl, genres, user?.preferred_music_genre]);
  useEffect(() => {
    if (!window.SC) {
      const script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      script.async = true;
      script.onload = () => {
        console.log('SoundCloud Widget API loaded');
      };
      document.body.appendChild(script);
    }
  }, []);

  // Initialize widget when iframe is ready
  const initWidget = useCallback(() => {
    if (iframeRef.current && window.SC) {
      try {
        widgetRef.current = window.SC.Widget(iframeRef.current);
        
        widgetRef.current.bind(window.SC.Widget.Events.READY, () => {
          console.log('Widget ready');
          setWidgetReady(true);
          // If autoplay was requested (from splash screen), play now
          if (pendingAutoplayRef.current) {
            pendingAutoplayRef.current = false;
            widgetRef.current.play();
          }
        });
        
        widgetRef.current.bind(window.SC.Widget.Events.PLAY, () => {
          setIsPlaying(true);
          window._rollm8sMusicPlaying = true;
          window.dispatchEvent(new CustomEvent('musicPlayerStateChange', { detail: { playing: true } }));
        });
        
        widgetRef.current.bind(window.SC.Widget.Events.PAUSE, () => {
          setIsPlaying(false);
          window._rollm8sMusicPlaying = false;
          window.dispatchEvent(new CustomEvent('musicPlayerStateChange', { detail: { playing: false } }));
        });
        
        widgetRef.current.bind(window.SC.Widget.Events.FINISH, () => {
          setIsPlaying(false);
          window._rollm8sMusicPlaying = false;
          window.dispatchEvent(new CustomEvent('musicPlayerStateChange', { detail: { playing: false } }));
        });
      } catch (error) {
        console.log('Widget init error:', error);
      }
    }
  }, []);

  // Re-initialize widget when URL changes
  useEffect(() => {
    setWidgetReady(false);
    const timeout = setTimeout(() => {
      initWidget();
    }, 1000);
    return () => clearTimeout(timeout);
  }, [soundcloudUrl, initWidget]);

  // Listen for nav pause request
  useEffect(() => {
    const handleNavPause = () => {
      if (widgetRef.current && widgetReady && isPlaying) {
        widgetRef.current.pause();
      }
    };
    window.addEventListener('pauseMusicForNav', handleNavPause);
    return () => window.removeEventListener('pauseMusicForNav', handleNavPause);
  }, [widgetReady, isPlaying]);

  const handlePlayPause = () => {
    if (widgetRef.current && widgetReady) {
      widgetRef.current.toggle();
    }
  };

  const handleRewind = () => {
    if (widgetRef.current && widgetReady) {
      widgetRef.current.getPosition((position) => {
        const newPosition = Math.max(0, position - 15000); // Rewind 15 seconds
        widgetRef.current.seekTo(newPosition);
      });
    }
  };

  const handleForward = () => {
    if (widgetRef.current && widgetReady) {
      widgetRef.current.getPosition((position) => {
        widgetRef.current.getDuration((duration) => {
          const newPosition = Math.min(duration, position + 15000); // Forward 15 seconds
          widgetRef.current.seekTo(newPosition);
        });
      });
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  useEffect(() => {
    // When user loads, set their preferred genre
    if (user?.preferred_music_genre && genres.length > 0) {
      const userGenre = genres.find(g => g.genre_id === user.preferred_music_genre);
      if (userGenre) {
        setSelectedGenre(userGenre);
        setSoundcloudUrl(userGenre.url);
        // Cache so next app-open is instant
        try {
          localStorage.setItem('rollm8s_music_genre', user.preferred_music_genre);
          userPrefRef.current = user.preferred_music_genre;
        } catch { /* ignore */ }
      }
    }
  }, [user, genres]);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/music-genres`);
      setGenres(response.data);

      // If genres exist, auto-select user's preferred genre or first genre.
      // `userPrefRef.current` is pre-seeded from localStorage so first paint
      // already matches the user's choice without waiting on the auth fetch.
      const preferredId = user?.preferred_music_genre || userPrefRef.current;
      if (response.data.length > 0) {
        if (preferredId) {
          const userGenre = response.data.find(g => g.genre_id === preferredId);
          if (userGenre) {
            setSelectedGenre(userGenre);
            setSoundcloudUrl(userGenre.url);
            return;
          }
        }
        // Default to Bashment genre, or first genre if not found
        const bashmentGenre = response.data.find(g => g.genre_id === 'bashment' || g.name?.toLowerCase() === 'bashment');
        const defaultGenre = bashmentGenre || response.data[0];
        setSelectedGenre(defaultGenre);
        setSoundcloudUrl(defaultGenre.url);
      } else {
        // No genres — fall back to site settings
        fetchSiteSettings();
      }
    } catch (error) {
      console.log('Failed to fetch genres, using default');
      fetchSiteSettings();
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      if (response.data?.music_player_url) {
        setSoundcloudUrl(response.data.music_player_url);
      }
    } catch (error) {
      console.log('Using default music URL');
    }
  };

  const selectGenre = async (genre) => {
    setLoading(true);
    setSelectedGenre(genre);
    setSoundcloudUrl(genre.url);
    setIsPlaying(false);
    setWidgetReady(false);

    // Cache locally so it survives the next reload even before the auth fetch returns
    try {
      localStorage.setItem('rollm8s_music_genre', genre.genre_id);
      userPrefRef.current = genre.genre_id;
    } catch { /* ignore */ }

    // Save preference if logged in
    if (user) {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        await axios.put(
          `${API}/users/me/music-preference?genre_id=${genre.genre_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (refreshUser) refreshUser();
      } catch (error) {
        console.log('Failed to save music preference');
      }
    }
    setLoading(false);
  };

  const encodedUrl = encodeURIComponent(soundcloudUrl);

  // Completely hidden state - show small button to bring back
  if (!isVisible) {
    return (
      <>
        {/* Hidden player - keeps playing */}
        <div className="fixed -left-[9999px] -top-[9999px]">
          <iframe 
            ref={iframeRef}
            key={soundcloudUrl}
            id="sc-widget"
            width="300" 
            height="166" 
            scrolling="no" 
            frameBorder="no" 
            allow="autoplay" 
            src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=%23d946ef&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=true&buying=false&sharing=false&download=false&show_artwork=true&show_playcount=false`}
            title="SoundCloud Music Player"
          />
        </div>
        <button
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={(e) => { if (!isDragging.current) showPlayer(); }}
          style={{ bottom: pos.bottom, left: pos.left }}
          className={`fixed z-50 w-12 h-12 rounded-full bg-primary/30 hover:bg-primary/60 text-white/60 hover:text-white flex items-center justify-center transition-colors hover:scale-110 ${hidePlayer_path ? 'hidden' : ''} cursor-grab active:cursor-grabbing touch-none`}
          title="Show Music"
          data-testid="music-show-btn"
        >
          <Music className="w-6 h-6" />
        </button>
      </>
    );
  }

  return (
    <div
      style={{ bottom: pos.bottom, left: pos.left, transition: 'bottom 200ms ease-out' }}
      className={`fixed z-50 transition-opacity duration-300 ${hidePlayer_path ? 'hidden' : ''}`}
      data-testid="global-music-player"
    >
      {/* Always render the iframe - just show/hide with CSS */}
      <div className={`absolute bottom-16 left-0 bg-card/95 backdrop-blur-md rounded-xl border border-border/50 shadow-2xl overflow-hidden min-w-[280px] transition-all duration-300 ${
        isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border/30 bg-primary/10">
          <span className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Music className="w-4 h-4 text-primary" />
            Pick Your Vibe
          </span>
          <button 
            onClick={() => setIsOpen(false)}
            className="text-muted-foreground hover:text-foreground p-1 hover:bg-muted/50 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Genre List */}
        {genres.length > 0 && (
          <div className="p-2 max-h-[180px] overflow-y-auto">
            <div className="space-y-1">
              {genres.map((genre) => (
                <button
                  key={genre.genre_id}
                  onClick={() => selectGenre(genre)}
                  disabled={loading}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    selectedGenre?.genre_id === genre.genre_id
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'hover:bg-muted/50 text-foreground'
                  }`}
                  data-testid={`genre-${genre.genre_id}`}
                >
                  <span className="text-xl">{genre.icon}</span>
                  <span className="font-medium flex-1 text-left">{genre.name}</span>
                  {selectedGenre?.genre_id === genre.genre_id && (
                    <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {isPlaying ? 'playing' : 'press play'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Player - Compact */}
        <div className="border-t border-border/30 p-2 bg-background/50">
          <div className="text-xs text-muted-foreground mb-1 px-1">
            {selectedGenre ? `Now: ${selectedGenre.name}` : 'Select a genre'}
          </div>
          <div className="bg-black/20 rounded-lg overflow-hidden">
            <iframe 
              ref={iframeRef}
              key={soundcloudUrl}
              id="sc-widget"
              width="100%" 
              height="20" 
              scrolling="no" 
              frameBorder="no" 
              allow="autoplay" 
              src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=%23d946ef&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false&buying=false&sharing=false&download=false&show_artwork=false&show_playcount=false`}
              title="SoundCloud Music Player"
            />
          </div>
        </div>

        {/* Custom Playback Controls */}
        <div className="border-t border-border/30 px-3 py-3 bg-gradient-to-r from-primary/10 to-secondary/10">
          <div className="flex items-center justify-center gap-3">
            {/* Rewind Button */}
            <button
              onClick={handleRewind}
              disabled={!widgetReady}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                widgetReady 
                  ? 'bg-primary/20 hover:bg-primary/40 text-primary hover:scale-110 active:scale-95' 
                  : 'bg-muted/20 text-muted-foreground cursor-not-allowed'
              }`}
              title="Rewind 15s"
              data-testid="music-rewind-btn"
            >
              <SkipBack className="w-5 h-5" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={!widgetReady}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${
                widgetReady
                  ? isPlaying
                    ? 'bg-gradient-to-br from-primary to-secondary text-white hover:scale-110 active:scale-95 shadow-primary/50'
                    : 'bg-gradient-to-br from-primary to-secondary text-white hover:scale-110 active:scale-95 shadow-primary/50 animate-pulse'
                  : 'bg-muted/30 text-muted-foreground cursor-not-allowed'
              }`}
              title={isPlaying ? 'Pause' : 'Play'}
              data-testid="music-playpause-btn"
            >
              {isPlaying ? (
                <Pause className="w-7 h-7" />
              ) : (
                <Play className="w-7 h-7 ml-1" />
              )}
            </button>

            {/* Forward Button */}
            <button
              onClick={handleForward}
              disabled={!widgetReady}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                widgetReady 
                  ? 'bg-primary/20 hover:bg-primary/40 text-primary hover:scale-110 active:scale-95' 
                  : 'bg-muted/20 text-muted-foreground cursor-not-allowed'
              }`}
              title="Forward 15s"
              data-testid="music-forward-btn"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          </div>
          
          {/* Status indicator */}
          <div className="text-center mt-2">
            <span className={`text-xs ${widgetReady ? 'text-primary' : 'text-muted-foreground'}`}>
              {widgetReady ? (isPlaying ? '♪ Playing' : 'Ready') : 'Loading...'}
            </span>
          </div>
        </div>

        {/* Hide Player Option */}
        <div className="border-t border-border/30 px-3 py-2">
          <button
            onClick={() => {
              setIsOpen(false);
              hidePlayer();
            }}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            Hide music player
          </button>
        </div>
      </div>

      {/* Single Icon Button — draggable */}
      <button
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onClick={(e) => { if (!isDragging.current) setIsOpen(!isOpen); }}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors hover:scale-110 shadow-lg border-2 cursor-grab active:cursor-grabbing touch-none ${
          isOpen 
            ? 'bg-primary text-white border-primary shadow-primary/50' 
            : isPlaying
              ? 'bg-primary/40 backdrop-blur-sm border-primary shadow-primary/50'
              : 'bg-primary/20 backdrop-blur-sm hover:bg-primary/40 border-primary/50 hover:border-primary shadow-primary/30'
        } ${!selectedGenre && !isOpen ? 'animate-pulse' : ''}`}
        title="Music - Pick Your Vibe!"
        data-testid="music-toggle-btn"
      >
        <span className="text-2xl">{selectedGenre?.icon || '🎵'}</span>
      </button>
    </div>
  );
};

export default GlobalMusicPlayer;
